import { fullyDecodeRouteParam } from '~/utils/route'

// Consume SSR payload on first read so subsequent fetches re-fetch normally.
// This is needed because defineAsyncComponent defers setup() past nuxtApp.isHydrating=false,
// so useAsyncData would otherwise ignore the SSR payload and re-fetch immediately.
function ssrPayloadCache(key: string, nuxtApp: ReturnType<typeof useNuxtApp>) {
  const data = nuxtApp.payload.data[key];
  if (data !== undefined) {
    Reflect.deleteProperty(nuxtApp.payload.data, key);
    return data;
  }
  return nuxtApp.static?.data?.[key];
}

export const usePageDetailApi = async <T>({
  path,
  previewPath,
  query,
}: {
  path: string;
  previewPath?: string;
  query?: Record<string, string>;
}) => {
  const nuxtApp = useNuxtApp();
  const route = useRoute();
  const previewStore = usePreviewStore();
  const isPreview = computed(() => previewStore.isPreview);
  const rawSlugDetail = route.params.slugDetail as string;
  const slugDetail = fullyDecodeRouteParam(rawSlugDetail);
  if (!slugDetail || slugDetail === 'undefined' || slugDetail === 'null') {
    throw createError({ statusCode: 404, statusMessage: 'Page not found' });
  }
  const encodedSlugDetail = encodeURIComponent(slugDetail);
  const queryId = route.query.id;
  const hasPreviewQuery = Boolean(previewPath && queryId && route.query.accessToken);
  const shouldFetchPreview = computed(() => isPreview.value || hasPreviewQuery);

  // Determine which API client to use based on preview mode
  const $api = nuxtApp.$api as typeof $fetch;
  const $apiBOF = nuxtApp.$apiBOF as typeof $fetch;
  const detailPath = shouldFetchPreview.value
    ? `${previewPath}${queryId}`
    : `${path}${encodedSlugDetail}`;
  const relatePath = `${path}${encodedSlugDetail}/relate`;
  const queryKey = JSON.stringify(query || {});

  const fetchDetail = async () => {
    const fetcher = shouldFetchPreview.value ? $apiBOF : $api;

    try {
      const response = await fetcher<ApiSuccessResponse<T>>(detailPath, { query });
      return response?.data;
    } catch (error: unknown) {
      const errorDetails = error as {
        statusCode?: unknown;
        response?: { status?: unknown };
      };
      const isNotFound = errorDetails.statusCode === 404 || errorDetails.response?.status === 404;
      const legacySlugPath = `${path}${encodeURIComponent(`${slugDetail}?`)}`;

      // Existing indexed URLs may have lost a trailing '?' because it was
      // emitted as a query delimiter instead of an encoded slug character.
      if (!shouldFetchPreview.value && isNotFound && !slugDetail.endsWith('?')) {
        const response = await $api<ApiSuccessResponse<T>>(legacySlugPath, { query });
        return response?.data;
      }

      throw error;
    }
  };

  // Fetch detail — use $api/$apiBOF ($fetch) directly to avoid nested useFetch hydration issues
  const { data: item } = await useAsyncData(
    `detail-${String(route.name)}-${slugDetail}-${queryKey}`,
    fetchDetail,
    {
      getCachedData: (key, nuxtApp) => ssrPayloadCache(key, nuxtApp),
    },
  );

  if (!item.value) {
    throw createError({ statusCode: 404, statusMessage: 'Page not found' })
  }

  // Fetch related items — use $api/$apiBOF ($fetch) directly
  const { data: relatedItems = [] } = await nuxtApp.runWithContext(() =>
    useAsyncData(
      `relate-${String(route.name)}-${slugDetail}`,
      async () => {
        if (shouldFetchPreview.value) {
          return [];
        }
        try {
          const response = await $api<ApiSuccessResponse<T[]>>(relatePath, { query });
          return response?.data || [];
        }
        catch {
          return [];
        }
      },
      {
        getCachedData: (key, nuxtApp) => ssrPayloadCache(key, nuxtApp),
      },
    ),
  );

  const { getBreadcrumb } = await nuxtApp.runWithContext(() =>
    useNavigationMenu(),
  );
  const breadcrumb = computed<BreadCrumb[]>(() => {
    const itemWithTitle = (item.value as { title?: string })?.title || '';
    return getBreadcrumb(itemWithTitle);
  });

  return {
    item,
    relatedItems,
    breadcrumb,
  };
};
